import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    trackElementClick,
    trackFormFieldInteraction,
    trackPageExit,
    trackPageView,
    trackScrollDepth,
    trackSectionView,
} from "../lib/analytics";

const AnalyticsTracker = () => {
    const location = useLocation();

    useEffect(() => {
        const timer = window.setTimeout(() => {
            trackPageView(window.location);
        }, 0);

        return () => window.clearTimeout(timer);
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        return () => {
            trackPageExit("route_change", {
                route_exit_to: `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`,
            });
        };
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        const onClick = (event) => {
            const element = event.target.closest?.("a[href],button,[role='button']");
            if (!element) return;
            trackElementClick(element);
        };

        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, []);

    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
                trackScrollDepth();
                ticking = false;
            });
        };

        trackScrollDepth();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        const onPageHide = () => trackPageExit("pagehide");
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                trackPageExit("visibilitychange");
            }
        };

        window.addEventListener("pagehide", onPageHide);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            window.removeEventListener("pagehide", onPageHide);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const onFocus = (event) => {
            const field = event.target.closest?.("input,select,textarea");
            if (!field) return;
            trackFormFieldInteraction(field, "focus");
        };

        const onBlur = (event) => {
            const field = event.target.closest?.("input,select,textarea");
            if (!field) return;
            trackFormFieldInteraction(field, "blur");
        };

        document.addEventListener("focusin", onFocus, true);
        document.addEventListener("focusout", onBlur, true);
        return () => {
            document.removeEventListener("focusin", onFocus, true);
            document.removeEventListener("focusout", onBlur, true);
        };
    }, []);

    useEffect(() => {
        if (!("IntersectionObserver" in window)) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || entry.intersectionRatio < 0.42) return;
                    trackSectionView(entry.target);
                    observer.unobserve(entry.target);
                });
            },
            { rootMargin: "0px 0px -12% 0px", threshold: [0.42, 0.66] },
        );

        const timer = window.setTimeout(() => {
            document
                .querySelectorAll("main > section, main [data-testid$='section'], main article[data-testid]")
                .forEach((element) => observer.observe(element));
        }, 120);

        return () => {
            window.clearTimeout(timer);
            observer.disconnect();
        };
    }, [location.pathname, location.search, location.hash]);

    return null;
};

export default AnalyticsTracker;
